import { AsyncPipe, NgClass, NgFor, NgIf, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  Signal,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  AppService,
  InAppNotificationService,
  PusherClientService,
  RouterAnimationNames,
  TenantAuthService,
  TenantNotificationService,
  WEB_CONFIG,
  WebAppEnvironment,
  fadeIn,
  slideFadeinRight,
  slideFadeinUp,
  zoomFadein,
} from '@buspro/web/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzDrawerComponent, NzDrawerContentDirective } from 'ng-zorro-antd/drawer';
import { NzDropDownDirective, NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { BehaviorSubject, Observable, Subscription, filter, lastValueFrom, take } from 'rxjs';

import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PusherChannels, TenantUserRoles } from '@buspro/shared/common';
import { AppNotificationDto, TenantUserNotificationsApi, TenantUsersApi, TenantUsersDto } from '@buspro/web/apis';
import { ThemeService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { NGXLogger } from 'ngx-logger';
import { MenuLeftNavComponent, TopBannerComponent } from '../../components';
import { CommandPaletteComponent } from '../../components/command-palette/command-palette.component';
import { WorkspaceHeaderComponent } from '../../components/workspace-header/workspace-header.component';
import { WorkspaceDataService } from '../../services/workspace-data.service';

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface BreadcrumbItem {
  label: string;
  route?: string;
}

@UntilDestroy()
@Component({
  selector: 'tenant-workspace',
  templateUrl: './tenant-workspace-layout.component.html',
  styleUrls: ['./tenant-workspace-layout.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NzBadgeModule,
    AsyncPipe,
    RouterOutlet,
    RouterLink,
    NzBreadCrumbModule,
    NzDropDownDirective,
    NzDropdownMenuComponent,
    NzDrawerComponent,
    NgOptimizedImage,
    NzDrawerContentDirective,
    MenuLeftNavComponent,
    NgIf,
    NgFor,
    TopBannerComponent,
    WorkspaceHeaderComponent,
    CommandPaletteComponent,
  ],
  // InAppNotificationService is deliberately NOT provided here. It used to be,
  // which gave this component its own instance while TenantNotificationService —
  // `providedIn: 'root'` — published into the root one. Nothing arriving live
  // ever reached the bar; a banner only appeared after a reload, when the
  // component's instance happened to read the same localStorage key. That is the
  // other half of "the banner is always there when I open the app".
  providers: [PusherClientService],
  animations: [slideFadeinUp, slideFadeinRight, zoomFadein, fadeIn],
})
export class TenantWorkspaceLayoutComponent implements OnInit, OnDestroy {
  private appService = inject(AppService);
  private usersNotificationsApi = inject(TenantUserNotificationsApi);
  private authService = inject(TenantAuthService);
  appNotificationService = inject(InAppNotificationService);
  private tenantUsersService = inject(TenantUsersApi);
  private logger = inject(NGXLogger);
  private route = inject(ActivatedRoute);
  protected readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);
  private ngxAuthService = inject(NgxAuthService);
  public tenantNotificationService = inject(TenantNotificationService);
  public themeService = inject(ThemeService);
  private workspaceDataService = inject(WorkspaceDataService);
  private queryClient = injectQueryClient();
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  @ViewChild(WorkspaceHeaderComponent) workspaceHeader?: WorkspaceHeaderComponent;
  @ViewChild('drawerContentRef', { read: ElementRef })
  drawerContentRef?: ElementRef<HTMLElement>;

  roles = TenantUserRoles;

  visible = false;
  animation = RouterAnimationNames.fadeIn;
  animationNames = RouterAnimationNames;
  authUser$: Observable<TenantUsersDto | null> = this.ngxAuthService.state;
  authUser: Signal<TenantUsersDto | null> = toSignal(this.authUser$, { initialValue: null });
  menuToggled$$ = new BehaviorSubject<boolean>(false);
  isOpen = true;
  sidebarCollapsed = signal(false);

  breadcrumbs = signal<BreadcrumbItem[]>([]);

  tenantPresence: Array<{
    tenantId: string;
    tenantName: string;
    onlineUsers: Array<{
      id: string;
      info: {
        name: string;
        tenantId: string;
        tenantName: string;
        terminalId?: string | null;
        role?: string;
      };
    }>;
  }> = [];
  private subscription: Subscription | null = null;
  private presenceSubscription: Subscription | null = null;
  private authUserSubscription: Subscription | null = null;
  onlineTenantUsers = signal(0);

  notificationsQuery = injectQuery(() => ({
    queryKey: ['notifications', this.authUser()?.id],
    queryFn: () => this.loadNotifications(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: false, // Disable polling - use Pusher for real-time updates
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  }));

  get notifications(): AppNotificationDto[] {
    return this.notificationsQuery.data() ?? [];
  }

  get unreadCount(): number {
    return this.notifications.filter((n) => !n.isRead).length;
  }

  get envName() {
    return this.environment.env;
  }

  get envColor() {
    const envName = this.environment.env;
    switch (envName) {
      case 'DEV':
        return 'warning';
      case 'STAGING':
        return 'success';
      case 'PROD':
        return 'processing';
      default:
        return 'warning';
    }
  }

  constructor(
    private changeDetectionRef: ChangeDetectorRef,
    private pusher: PusherClientService,
  ) {
    // Generate breadcrumbs from route and close mobile drawer on navigation
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateBreadcrumbs();
      if (this.visible) {
        this.visible = false;
        this.changeDetectionRef.detectChanges();
        setTimeout(() => this.workspaceHeader?.focusMobileMenuTrigger(), 350);
      }
    });

    this.updateBreadcrumbs();

    this.checkAndInitializeBrowserNotificationFeature();
  }

  private updateBreadcrumbs() {
    // Drop the query string and fragment before splitting: `router.url` carries them, so a
    // page that keeps state in the URL used to render its whole query string as the last
    // crumb — "Completed?terminal=c663c006 D79e 4c1d A8f2 89f8e0a29b71", because the
    // segment was then title-cased on its hyphens.
    const url = this.router.url.split(/[?#]/)[0];
    const segments = url.split('/').filter((s) => s);
    const breadcrumbItems: BreadcrumbItem[] = [{ label: 'Home', route: '/dashboard' }];

    if (segments.length === 0) {
      this.breadcrumbs.set(breadcrumbItems);
      return;
    }

    // Build breadcrumbs from route segments
    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += '/' + segment;
      // A raw record id means nothing to a reader, and nothing routes to it alone.
      if (UUID_SEGMENT.test(segment)) return;
      const label = this.formatSegmentLabel(segment);
      if (index === segments.length - 1) {
        breadcrumbItems.push({ label });
      } else {
        breadcrumbItems.push({ label, route: currentPath });
      }
    });

    this.breadcrumbs.set(breadcrumbItems);
  }

  private formatSegmentLabel(segment: string): string {
    return segment
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  ngOnInit() {
    // A banner that leaves the screen — clicked away, or resolved because the
    // user reached the page it was pointing at — is marked read on the server, so
    // the bar and the bell's unread count stop telling two different stories.
    this.appNotificationService.dismissed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((banner) => {
      const userId = this.authUser()?.id;
      if (!userId) {
        return;
      }
      this.usersNotificationsApi
        .tenantUsersNotificationControllerMarkNotificationAsRead({ id: userId, notificationId: banner.id })
        .subscribe({
          next: () => this.queryClient.invalidateQueries({ queryKey: ['notifications'] }),
          error: (error) => this.logger.error('Could not mark the dismissed banner as read', error),
        });
    });

    // NOTE: this used to also subscribe to the PUBLIC 'schedules' channel and
    // raise its own banner plus its own browser notification from it. Two
    // problems: it duplicated logic that already lives in
    // BaseNotificationService (two copies of one rule, free to drift), and the
    // channel is global with no tenant in its name, so anything ever published
    // there would have been announced to every tenant on the platform. New
    // schedules already arrive on the user's private channel, which is where the
    // single handler picks them up.

    // Subscribe to user-specific notifications
    this.authUserSubscription = this.authUser$.subscribe((data) => {
      if (data?.id) {
        // Scope any stored banner to this user; a shared browser must not show
        // the previous user's messages.
        this.appNotificationService.setUser(data.id);

        // Single subscription handles ALL private notifications
        this.tenantNotificationService.subscribeToUserNotifications(data.id);

        // Clean up previous presence subscription if exists
        if (this.subscription) {
          this.subscription.unsubscribe();
          this.subscription = null;
        }

        // Helper function to subscribe to presence channel
        const subscribeToPresence = () => {
          this.logger.log('[Pusher] Connection ready, subscribing to presence channel');

          // Subscribe to presence channel for online users
          this.pusher.subscribeToPresenceChannel('online-users');

          // Subscribe to presence updates
          this.subscription = this.pusher.tenantPresence.subscribe((tenantPresence) => {
            // Filter to only include current tenant's users
            const currentTenant = tenantPresence.find((tenant) => tenant.tenantId === data.company.id);

            // Only pass current tenant data to the component (not all tenants)
            this.tenantPresence = currentTenant ? [currentTenant] : [];

            const onlineUsers = currentTenant?.onlineUsers.length || 0;
            this.onlineTenantUsers.set(onlineUsers);

            // Update workspace data service for dashboard and other components
            this.workspaceDataService.updateOnlineUsers(onlineUsers, this.tenantPresence, data?.company?.id || null);
          });
        };

        // If already connected, subscribe immediately
        if (this.pusher.connectionState.value === 'connected') {
          subscribeToPresence();
        } else {
          // Wait for Pusher to be connected before subscribing to presence channel
          const connectionState$ = this.pusher.connectionState.pipe(
            filter((state) => state === 'connected'),
            take(1),
          );

          this.presenceSubscription = connectionState$.subscribe(() => {
            subscribeToPresence();
          });
        }
      } else {
        // User logged out or not authenticated - cleanup presence subscription
        this.logger.log('[Pusher] User not authenticated, cleaning up presence subscription');
        if (this.subscription) {
          this.subscription.unsubscribe();
          this.subscription = null;
        }
        const presenceChannel = this.pusher.getPresenceChannel();
        if (presenceChannel) {
          try {
            presenceChannel.unsubscribe();
          } catch (error) {
            this.logger.error('[Pusher] Error unsubscribing presence channel on user logout:', error);
          }
        }
        this.tenantPresence = [];
        this.onlineTenantUsers.set(0);

        // Clear workspace data service
        this.workspaceDataService.updateOnlineUsers(0, [], null);
      }
    });
  }

  loadNotifications(): Promise<AppNotificationDto[]> {
    const userId = this.authUser()?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }
    // Use tenant-specific notification endpoint
    return lastValueFrom(
      this.usersNotificationsApi.tenantUsersNotificationControllerGetUserNotifications({
        id: userId,
      }),
    ).then((result) => result ?? []);
  }

  logout() {
    // Drop any banner with it: on a shared terminal the next person to sign in
    // must not inherit this user's messages.
    this.appNotificationService.setUser(null);
    this.ngxAuthService.logout();
    this.authService.logout();
  }

  toggle() {
    this.visible = !this.visible;
    this.changeDetectionRef.detectChanges();
  }

  /** Called when the mobile drawer closes (X or mask). Restores focus to the hamburger button. */
  onDrawerClose(): void {
    this.visible = false;
    this.changeDetectionRef.detectChanges();
    setTimeout(() => this.workspaceHeader?.focusMobileMenuTrigger(), 350);
  }

  /** Called when the mobile drawer opens. Moves focus into the drawer for accessibility. */
  onDrawerOpen(): void {
    setTimeout(() => this.drawerContentRef?.nativeElement?.focus(), 100);
  }

  /** Arrow so `this` is always the layout when header calls this callback. */
  openMobileMenu = (): void => {
    this.visible = true;
    this.changeDetectionRef.detectChanges();
  };

  /** No-op for unused menu outputs when used inside mobile drawer. */
  noop(_?: boolean): void {
    // intentionally empty: no-op for unused menu outputs in mobile drawer
  }

  routeAnimation(outlet: RouterOutlet, animation: string) {
    if (animation === this.animation) {
      return outlet.isActivated && outlet.activatedRoute.routeConfig?.path;
    }
    return RouterAnimationNames.none;
  }

  navigateToSettings() {
    this.router.navigate(['/settings/company']);
  }

  toggleSideMenu($event: boolean) {
    this.menuToggled$$.next($event);
    this.isOpen = $event;
    this.changeDetectionRef.detectChanges();
    this.appService.isSideMenuOpen.next($event);
  }

  ngOnDestroy() {
    this.logger.log('[Pusher] Component destroying, cleaning up subscriptions');

    // Unsubscribe from notification service
    this.tenantNotificationService.unsubscribeAll();

    // Unsubscribe from presence updates
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    // Unsubscribe from presence channel connection state waiting
    if (this.presenceSubscription) {
      this.presenceSubscription.unsubscribe();
      this.presenceSubscription = null;
    }

    // Unsubscribe from auth user changes
    if (this.authUserSubscription) {
      this.authUserSubscription.unsubscribe();
      this.authUserSubscription = null;
    }

    // Unsubscribe from presence channel
    const presenceChannel = this.pusher.getPresenceChannel();
    if (presenceChannel) {
      try {
        presenceChannel.unsubscribe();
      } catch (error) {
        this.logger.error('[Pusher] Error unsubscribing presence channel on destroy:', error);
      }
    }

    // Clear presence data
    this.tenantPresence = [];
    this.onlineTenantUsers.set(0);

    // Clear workspace data service
    this.workspaceDataService.updateOnlineUsers(0, [], null);
  }

  reconnectNotifications() {
    const userId = this.authUser()?.id;
    if (userId) {
      this.tenantNotificationService.reconnect(userId);
    }
  }

  private async checkAndInitializeBrowserNotificationFeature(): Promise<void> {
    if (!('Notification' in window)) {
      this.logger.warn('This browser does not support desktop notification');
      return;
    }

    if (Notification.permission === 'granted') {
      this.logger.log('Notification permission already granted');
    } else if (window.Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.logger.log(`Notification permission ${permission}`);
    }
  }
}
